import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Grid,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { STATUS } from "../../../redux/constants";
import {
  resetAddCombustionStatus,
  deleteEmissions,
} from "../../../redux/actions";
import CeroButton from "../../../components/CeroButton";
import ListComments from "../ListComment";
import ListAuditTrails from "../ListAuditTrails";
import ListEmissionFiles from "../ListEmissionFiles";
import ListTicketDrawer from "../../common/ListTicketsDrawer";
import CeroConfirmDrawer from "../../../components/CeroConfirmDrawer";
import useStyles from "./styles";
import TicketIcon from "../../../assets/icons/TicketIcon.png";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { getCookie } from "../../../services/cookie";
import CeroItemPair from "../../../components/CeroItemPair";
import CeroInfoPair from "../../../components/CeroInfoPair";
import EmissionDetailsApprovalHeader from "../EmissionApprovalHeader";

const CapitalGoods = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const readOnlyMode = getCookie("read_only_mode") === "disable_edit";

  const {
    emissionId,
    emissionData,
    onCancel,
    isDeleteEnable,
    setIsDrawerOpen,
    isAuditor,
    company,
    subType,
    approvalId,
  } = props;

  const [value, setValue] = useState(0);
  const [showTickets, setShowTickets] = useState(false);
  const [displayWarning, setDisplayWarning] = useState(false);

  const deleteEmissionData = useSelector(
    (state) => state.emission.deleteEmissions
  );

  useEffect(() => {
    if (deleteEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar("Downstream T and D record deleted successfully", {
        variant: "success",
      });
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (deleteEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        deleteEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetAddCombustionStatus());
    }
  }, [deleteEmissionData, enqueueSnackbar, onCancel, dispatch]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const onConfirmDelete = () => {
    const requestData = {
      id: emissionId,
    };
    dispatch(deleteEmissions(requestData));
  };

  const onclickShowTickets = () => {
    setShowTickets(true);
  };

  const onCloseTickets = () => {
    setShowTickets(false);
  };

  const onUpdatePurchasedElectricity = () => {
    navigate(`/emissions/edit/capital_goods/${emissionId}`);
  };
  const monthDict = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  };
  return (
    <>
      <div className={classes.headerContainer}>
        <div
          className={classes.backContainer}
          onClick={() => {
            navigate(-1);
          }}
        >
          <ArrowBackIosIcon />
          Back
        </div>
        <Box className={classes.alignButton}>
          <EmissionDetailsApprovalHeader
            emissionId={emissionId}
            can_review_action_do={emissionData.can_review_action_do}
            subType={subType}
            approvalId={approvalId}
          />
          <CeroButton
            buttonText="Raise a ticket"
            className={classes.buttonPrimary}
            onClick={() => setIsDrawerOpen(true)}
            disabled={readOnlyMode}
          />
          <IconButton onClick={onclickShowTickets} disabled={readOnlyMode}>
            <img src={TicketIcon} alt="Logo" width="25" height="25" />
          </IconButton>
        </Box>
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Box className={classes.header}>
            <Typography variant="h6" component="div">
              Capital Goods
            </Typography>
          </Box>
          <Box className={classes.topContainer}>
            <Grid
              container
              direction={"row"}
              wrap="nowrap"
              justifyContent={"space-between"}
              display={"contents"}
              spacing={12}
            >
              <Grid
                item
                container
                direction={"column"}
                display={"contents"}
                xs={10}
              >
                <Box className={classes.actionBox}>
                  <CeroInfoPair
                    title="Status:"
                    value={
                      emissionData.status
                        ? emissionData.status === "pending_approval"
                          ? "pending approval"
                          : emissionData.status
                        : "NA"
                    }
                    classes={{
                      container: classes.statusContainer,
                      title: classes.statusTitle,
                      value:
                        emissionData.status === "In Progress" ||
                        emissionData.status === "pending_approval"
                          ? classes.Inprogress
                          : classes[emissionData?.status],
                    }}
                  />
                </Box>
                <CeroItemPair
                  title="Facility:"
                  value={
                    emissionData.facility_name
                      ? emissionData.facility_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />

                <CeroItemPair
                  title="Month and Year:"
                  value={
                    emissionData.year && emissionData.month
                      ? monthDict[emissionData.month] + " " + emissionData.year
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />

                <CeroItemPair
                  title="Calculation Method:"
                  value={
                    emissionData.calculation_method_name
                      ? emissionData.calculation_method_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                {emissionData?.calculation_method_name?.toLowerCase() ===
                "supplier-specific method" ? (
                  <>
                    <CeroItemPair
                      title="Quantity of goods:"
                      value={
                        emissionData.quantity_of_purchased_goods
                          ? emissionData.quantity_of_purchased_goods
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Supplier specific emission factor of capital goods:"
                      value={
                        emissionData.supplier_specific_product_ef
                          ? emissionData.supplier_specific_product_ef
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "spend-based" ? (
                  <>
                    <CeroItemPair
                      title="Company name:"
                      value={
                        emissionData.vendor_company_name
                          ? emissionData.vendor_company_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Sector:"
                      value={
                        emissionData.sector_name
                          ? emissionData.sector_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Industry:"
                      value={
                        emissionData.industry_name
                          ? emissionData.industry_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Industry sub category:"
                      value={
                        emissionData.industry_sub_category_name
                          ? emissionData.industry_sub_category_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Value of goods:"
                      value={
                        emissionData.value_of_purchased_goods
                          ? `${emissionData.value_of_purchased_goods} ${emissionData.currency}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "average-data method" ? (
                  <>
                    <CeroItemPair
                      title="Mass of capital goods:"
                      value={
                        emissionData.mass_of_purchased_goods
                          ? emissionData.mass_of_purchased_goods
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Unit:"
                      value={
                        emissionData.mass_unit ? emissionData.mass_unit : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Emission factor of capital goods per unit of mass:"
                      value={
                        emissionData.ef_of_purchased_goods_per_mass
                          ? emissionData.ef_of_purchased_goods_per_mass
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : (
                  <></>
                )}
                {emissionData?.audited_by && (
                  <>
                    <CeroItemPair
                      title="Audited by:"
                      value={
                        emissionData.audited_by ? emissionData.audited_by : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Audited on:"
                      value={
                        emissionData.audited_on
                          ? dayjs(emissionData.audited_on).format("DD/MM/YYYY")
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.bottomContainer}>
            <Typography
              variant="h6"
              component="h6"
              className={classes.previewTitle}
            >
              Emission
            </Typography>

            <Grid container direction="row" spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.co2 != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>: {emissionData.co2} tonnes
                  </Typography>
                )}
                {emissionData?.ch4 != null && (
                  <Typography className={classes.previewItem}>
                    CH<sub>4</sub>: {emissionData.ch4} tonnes
                  </Typography>
                )}
                {emissionData?.biofuel_co2 != null && (
                  <Typography className={classes.previewItem}>
                    BioFuel CO<sub>2</sub>: {emissionData.biofuel_co2} tonnes
                  </Typography>
                )}
                {emissionData?.co2e != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>e: {emissionData.co2e} tonnes
                  </Typography>
                )}
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.n2o != null && (
                  <Typography className={classes.previewItem}>
                    N<sub>2</sub>O: {emissionData.n2o} tonnes
                  </Typography>
                )}
                {emissionData?.ef != null && (
                  <Typography className={classes.previewItem}>
                    EF: {emissionData.ef} kgCO<sub>2</sub>e/unit
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box className={classes.buttonContainer}>
          {isDeleteEnable && (
            <CeroButton
              buttonText={<DeleteOutlineIcon />}
              className={clsx(classes.button, classes.deleteButton)}
              onClick={() => setDisplayWarning(true)}
              disabled={readOnlyMode}
            />
          )}
          {emissionData?.is_able_to_update === true && (
            <CeroButton
              buttonText="Update"
              className={clsx(classes.button, classes.buttonPrimary)}
              onClick={onUpdatePurchasedElectricity}
              disabled={readOnlyMode}
            />
          )}
        </Box>
        <Box
          className={classes.tabContainer}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tabs
            value={value}
            className={classes.tabs}
            onChange={handleChange}
            aria-label="emission tabs"
          >
            <Tab label="Comments" id="comments" />
            <Tab label="Attachment" id="files" />
            <Tab label="Change Logs" id="audit-history" />
          </Tabs>
        </Box>
        {value === 0 && (
          <ListComments
            emissionId={emissionId}
            isAuditor={isAuditor}
            company={company}
          />
        )}
        {value === 2 && (
          <ListAuditTrails
            emissionId={emissionId}
            isAuditor={isAuditor}
            company={company}
          />
        )}
        {value === 1 && (
          <ListEmissionFiles
            emissionId={emissionId}
            isAuditor={isAuditor}
            company={company}
          />
        )}
        {displayWarning && (
          <CeroConfirmDrawer
            isOpen={displayWarning}
            onClose={() => setDisplayWarning(false)}
            onConfirm={onConfirmDelete}
          />
        )}
        {showTickets && (
          <ListTicketDrawer
            isOpen={showTickets}
            scope="emission"
            scopeId={emissionId}
            onClose={onCloseTickets}
          />
        )}
      </Container>
    </>
  );
};

export default CapitalGoods;
